/**
 * @module  Experience/Shared-Browse
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:max-file-line-count */
// Core Libraries
import { Component, Input, Output, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Wove } from 'aspect.js/';

// Services
import { SharedBrowseService } from '@experience/shop/shared-browse/services/shared-browse.service';
import { CommonApiService } from '@support/shared/services/common-api/common-api.service';

// Interfaces
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { SelectItem } from '@support/shared/interfaces/select-item.interface';
import { BrowseEntry } from '@experience/shop/shared-browse/interfaces/browse-entry.interface';
import { BrowseEntryConfigData } from '@core/aem/interfaces/common/browse-entry.interface';
import { BrowseEntryChangeInterface } from '@experience/shop/shared-browse/interfaces/browse-entry-change.interface';
import { ChangeEntryAEMInterface } from '@experience/shop/shared-browse/interfaces/change-entry-aem.interface';
import { APIError } from '@support/api/common/error.interface';
import { TmoButton } from '@support/shared/widgets/interfaces/button.interface';
import { DynamicModal } from '@support/shared/widgets/interfaces/dynamic-modal.interface';
import { Address } from '@support/api/proxies/responses/api-2004.interface';

// Constants
import {
  SHARED_BROWSE_CONSTANTS,
  SHARED_BROWSE_COMPONENTS,
  BillingTypeEnum,
  ZIP_CODE_CONSTANTS,
} from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { throwError as observableThrowError, Subject } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { FieldValidationItem } from '@support/shared/models/field-validation-item.model';
import { AppConfig } from '@root/app.config';
/**
 * Component contains 3 sections to change ZipCode, BillingType and Credit Level
 */
@Wove({ moduleName: SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE })
@Component({
  selector: 'tmo-change-entry',
  templateUrl: './change-entry.component.html',
})
export class ChangeEntryComponent implements OnInit, OnDestroy {
  /**
   * AEM content from parent pages to fill in text for change-entry component
   */
  @Input() public aemContent: ChangeEntryAEMInterface;
  /**
   * Boolean to allow showing of only the zipCode section. For the Details pages
   */
  @Input() public zipOnly?: boolean;
  /**
   * variable to store rap channel check
   */
  @Input() public isRap: boolean;
  /**
   * Boolean to allow showing of and hiding of change zip code link
   */
  @Input() public hideChangeZipCodeLink: boolean;
  /**
   * Outputs events on changes to zip, billingType or creditLevel
   */
  @Output() public browseEntryChange: EventEmitter<BrowseEntryChangeInterface>;
  /**
   * Stores changeZip Form
   */
  public changeZipForm: FormGroup;
  /**
   * Stores changeCreditLevel Form
   */
  public changeCreditLevelForm: FormGroup;
  private addressApiResponse: Address;
  /**
   * Stores changeCreditLevel Form
   */
  public changeBillingLevelForm: FormGroup;
  /**
   * Stores the current state of the entry component being changed.
   * - For example if user selects changeZip, currentState will be 'changeZip'
   * - If nothing is currently being changed, state will be null
   */
  public changeState: string;
  /**
   * Tracks if any changes have been submitted with zipCode
   */
  public zipChangeSubmitted: boolean;
  /**
   * Tracks if any changes have been submitted with creditLevel
   */
  public creditLevelChangeSubmitted: boolean;
  /**
   * Tracks if any changes have been submitted with creditLevel
   */
  public billingLevelChangeSubmitted: boolean;
  /**
   * Stores constants for the sharedBrowseModule
   */
  public sharedBrowseConstants: ObjectInterface;

  /**
   * Constants for identifier keys
   */
  public fieldValidationConstants: ObjectInterface;
  /**
   * Constant values for creditLevels (excellent, average, building)
   */
  public creditLevels: SelectItem[];

  /**
   * Constant values for creditLevels (excellent, average, building)
   */
  public billingLevels: SelectItem[];
  /**
   * Constant value for creditLevelMapping
   */
  public creditLevelMapping: ObjectInterface;
  /**
   * Constant value for billingTypeMapping
   */
  public billingTypeMapping: ObjectInterface;

  /**
   * User to store the ENUM Value for prepaid to do check
   */
  public prepaidBillingEnum: Object;
  /**
   * Selected values for browse entry
   */
  public selectedBrowseEntry: BrowseEntry;

  public creditCheckSubmitted: Boolean;

  /**
   * AEM data specific to browse entry component
   */
  public browseEntryAEMData: BrowseEntryConfigData;
  /**
   * isByodFlow of type boolean to check flow is byod or not
   */
  @Input() public isByodFlow: boolean;
  /**
   * If multiline then disable zipCode changes
   */
  @Input() public isMultiline: boolean;
  /**
   * isByodFlow of type boolean to check flow is isProductSelectorFlow or not
   */
  @Input() public isProductSelectorFlow: boolean;
  /**
   * Zip Code pattern
   */
  public zipCodeRegexPattern: string;
  /**
   * canEditCreditType  of type boolean to check whether can edit or not
   */
  @Input() public canEditCreditType?: boolean;
  /**
   * canEditBillingType  of type boolean to check whether can edit or not
   */
  @Input() public canEditBillingType?: boolean;
  /**
   * to check whether navigated from browse plan Page
   */
  @Input() public isBrowsePlan?: boolean;
  /**
   * isBillingTypeChanged  of type boolean to check whether billing type has changed or not
   */
  public isBillingTypeChanged: boolean;

  /**
   * isCreditLevelChanged  of type boolean to check whether credit level has changed or not
   */
  public isCreditLevelChanged: boolean;

  /**
   * to check whether navigated from Line Landing Page
   */
  public isCustomerAvailable: boolean;
  /**
   * to check whether navigated from Line Landing Page
   */
  public billingAddressZip: string;
  /**
   * to check if is anonnymous scenario
   */
  public isAnon: boolean;
  /**
   * isZIpCodeChanged  of type boolean to check whether zipCode type has changed or not
   */
  public isZipCodeChanged: boolean;
  /**
   * Flag to check whether to hide or show change hyperlink
   */
  public showChangeLink: boolean;
  /**
   * Update Zip Code primary button
   */
  public updateZipCodeButton: TmoButton;
  /**
   * Cancel Zip code change button
   */
  public cancelButton: TmoButton;
  /**
   * Unsubscriber for all subscribtions
   */
  public showSpinner: boolean;
  /**
   * Stores Zip Code Change Modal
   */
  public zipCodeModal: DynamicModal;
  private ngUnsubscribe: Subject<void>;

  private zipCodeFieldValidation: FieldValidationItem;
  private resetValueOnUpdate: boolean;

  /**
   * Store flag to disable confirm zip code change CTA
   */
  public disableConfirm: boolean;

  /**
   * Store flag to display zip code error
   */
  public apiError: boolean;
  private zipCodeExpiryHrs: number;

  constructor(
    private sharedBrowseService: SharedBrowseService,
    private cartSupportService: CartSupportService,
    private stateUtilService: StateStoreUtilsService,
    private objectService: ObjectService,
    private commonApiService: CommonApiService,
    private appConfig: AppConfig
  ) {
    this.changeState = null;
    this.browseEntryChange = new EventEmitter();
    this.creditCheckSubmitted = false;
    this.canEditCreditType = true;
    this.canEditBillingType = true;
    this.showChangeLink = false;
    this.ngUnsubscribe = new Subject<void>();
    this.showSpinner = false;
    this.disableConfirm = true;
  }
  public ngOnInit(): void {
    if (!this.appConfig.getConfig('disableChangeBrowseZipCode')) {
      const hrs = this.appConfig.getConfig('zipCodeCacheExpiryHrs');
      this.zipCodeExpiryHrs = !hrs || isNaN(hrs) ? ZIP_CODE_CONSTANTS.CACHE_HOURS : Number(hrs);
      this.billingType();
      this.initData();
      this.loadChangeEntryComponent();
      this.checkCreditState();
      this.checkCustomerAvailable();
      this.updateZipCodeButton = this.setButton(true, 'tmo-button-updatezip');
      this.cancelButton = this.setButton(false, 'tmo-button-cancel');
      this.isAnon = this.sharedBrowseService.isAnon();
      this.zipCodeModal = {
        disableDismiss: false,
        disableEsc: true,
        toggleModalEmitter: new EventEmitter(),
        dismissableMask: true,
        modalName: 'update-location-modal',
      };
    }
  }

  /**
   * Function to check the billing type needed to show the change hyperlink
   *
   */
  public billingType(): void {
    this.showChangeLink =
      this.stateUtilService.getBillingTypeOfCustomerInFocus() === SHARED_BROWSE_CONSTANTS.POSTPAID;
  }

  /**
   * Function triggered when user selects to changeZip
   */
  public changeZip(): void {
    // Only allow for change if the currentChange state is null meaning we are not
    // changing billingType or creditLevelType
    if (!this.changeState && !this.isMultiline) {
      this.zipChangeSubmitted = false;
      this.changeState = this.sharedBrowseConstants.changeZipState;
      this.resetValueOnUpdate = true;
    }
  }
  /**
   * Call service to check if a credit check has been performed
   */
  public checkCreditState(): void {
    this.creditCheckSubmitted = this.sharedBrowseService.checkCustomerCredit();
  }
  /**
   * Function triggered for changing creditLevel. Displays the changeCredit flags and text
   */
  public changeCreditLevel(): void {
    // Only allow for change if the currentChange state is null meaning we are not
    // changing billingType or creditLevelType
    if (!this.changeState) {
      this.creditLevelChangeSubmitted = false;
      this.changeState = this.sharedBrowseConstants.changeCreditLevelState;
    }
  }
  /**
   * Function triggered for changing BillingLevel. Displays the changeBilling flags and text
   */
  public changeBillingLevel(): void {
    if (!this.changeState) {
      this.billingLevelChangeSubmitted = false;
      this.changeState = this.sharedBrowseConstants.billingTypeChanged;
    }
  }
  /**
   * Called on submission of Billing level. Stores values and emits a change event
   */
  public submitBillingLevelChange(): void {
    if (this.changeBillingLevelForm.controls.billingType.valid) {
      const decimalRadix = 10;
      const storedEntryState = this.getStoredEntryState();
      storedEntryState.billingType = parseInt(
        this.changeBillingLevelForm.controls.billingType.value,
        decimalRadix
      );
      this.sharedBrowseService.setUserInputsConfigData(storedEntryState);
      const previousSelectedBillingType = this.selectedBrowseEntry
        ? this.selectedBrowseEntry.billingType
        : null;
      this.loadCurrentEntryState();
      this.isBillingTypeChanged =
        previousSelectedBillingType !==
        (this.selectedBrowseEntry ? this.selectedBrowseEntry.billingType : null);
      if (this.isBillingTypeChanged) {
        this.loadChangeEntryComponent();
        this.browseEntryChange.emit({ changeId: SHARED_BROWSE_CONSTANTS.billingTypeChanged });
      }
    }
  }
  /**
   * Called on submission of credit level change. Stores values and emits a change event
   */
  public submitCreditLevelChange(): void {
    if (this.changeCreditLevelForm.controls.creditLevel.valid) {
      const decimalRadix = 10;
      const storedEntryState = this.getStoredEntryState();
      storedEntryState.creditLevel = parseInt(
        this.changeCreditLevelForm.controls.creditLevel.value,
        decimalRadix
      );
      this.sharedBrowseService.setUserInputsConfigData(storedEntryState);
      const previousSelectedCreditLevel = this.selectedBrowseEntry.creditLevel;
      this.loadCurrentEntryState();
      this.isCreditLevelChanged =
        previousSelectedCreditLevel !== this.selectedBrowseEntry.creditLevel;
      if (this.isCreditLevelChanged) {
        this.loadChangeEntryComponent();
        this.browseEntryChange.emit({ changeId: SHARED_BROWSE_CONSTANTS.creditLevelChanged });
      }
    }
  }
  /**
   * Function triggered when user selects to change zip. Calls service to set state only if input form is valid
   */
  public submitZipChange(): void {
    if (this.changeZipForm.controls.zip.valid) {
      this.zipChangeSubmitted = true;
      this.creditLevelChangeSubmitted = false;
      this.billingLevelChangeSubmitted = false;
      this.changeState = null;
      const storedEntryState = this.stateUtilService.getBillingAddress();
      storedEntryState.addressid = this.addressApiResponse.addressid;
      storedEntryState.postalCode = this.addressApiResponse.postalCode;
      storedEntryState.cityName = this.addressApiResponse.cityName;
      storedEntryState.stateCode = this.addressApiResponse.stateCode;
      const userInputsConfigData: BrowseEntry = this.sharedBrowseService.getUserInputsConfigData();
      userInputsConfigData.addresses.addressid = this.addressApiResponse.addressid;
      userInputsConfigData.zip = this.addressApiResponse.postalCode;
      userInputsConfigData.cityName = this.addressApiResponse.cityName;
      userInputsConfigData.stateCode = this.addressApiResponse.stateCode;
      this.sharedBrowseService.setUserInputsConfigData(userInputsConfigData);
      this.sharedBrowseService.updateBrowseZipCode(this.addressApiResponse);
      const previousSelectedZipCode = this.selectedBrowseEntry.zip;
      this.loadCurrentEntryState();
      this.isZipCodeChanged = previousSelectedZipCode !== this.selectedBrowseEntry.zip;
      if (this.isZipCodeChanged) {
        this.sharedBrowseService.enableSetStoredZipCode();
      }
      // added delete cart for zip change after robert confirmation QC628093
      if (
        this.isZipCodeChanged &&
        this.stateUtilService.getCart() &&
        this.stateUtilService.getCart().cartId &&
        this.stateUtilService.getCart().cartId !== ''
      ) {
        this.showSpinner = true;
        this.cartSupportService
          .deleteCart()
          .pipe(
            catchError((err: APIError) => {
              this.showSpinner = false;
              return observableThrowError(err);
            }),
            takeUntil(this.ngUnsubscribe)
          )
          .subscribe(() => {
            this.showSpinner = false;
            this.cartSupportService.updateCart.emit(true);
            this.zipCodeChange();
          });
      } else {
        this.zipCodeChange();
      }
    }
  }

  /**
   * Unsubscribes for all subscriptions
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  /**
   * Called on submission of Billing level. Stores values and emits a change event
   */
  public changeBillingType(): void {
    if (this.stateUtilService.getCart()) {
      this.cartSupportService
        .deleteCart()
        .pipe(
          catchError((err: APIError) => {
            return observableThrowError(err);
          }),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(() => {
          this.submitBillingLevelChange();
        });
    } else {
      this.submitBillingLevelChange();
    }
  }
  /**
   * Initializes and loads data for the component
   */
  private initData(): void {
    // Common constants
    this.fieldValidationConstants = this.sharedBrowseService.getFieldValidationConstants();
    if (this.fieldValidationConstants) {
      this.zipCodeRegexPattern = this.sharedBrowseService.getRegexPatternForZipCode(
        this.fieldValidationConstants.ZIP_CODE
      );
    }
    this.sharedBrowseConstants = this.sharedBrowseService.getSharedBrowseConstants();
    this.prepaidBillingEnum = BillingTypeEnum.PREPAID;
    this.zipCodeFieldValidation = this.commonApiService.getFieldValidation(
      this.fieldValidationConstants.ZIP_CODE
    );
  }
  /**
   * Loads selected values for browse entry
   */
  private loadCurrentEntryState(): void {
    this.selectedBrowseEntry = this.sharedBrowseService.getUserInputsConfigData();
    const billingAddress = this.stateUtilService.getBillingAddress();
    const zip =
      billingAddress.postalCode || this.objectService.get(this.selectedBrowseEntry, 'zip');
    const stateCode = billingAddress.stateCode;
    this.selectedBrowseEntry.addresses.addressid = billingAddress.addressid;
    this.selectedBrowseEntry.cityName = billingAddress.cityName;
    this.selectedBrowseEntry.stateCode = stateCode;
    this.selectedBrowseEntry.zip = zip.split('-')[0];
  }
  /**
   * Loads Forms Data for the change Entry Component
   */
  private loadChangeEntryComponent(): void {
    this.changeZipForm = this.sharedBrowseService.buildChangeZipForm();
    this.sharedBrowseService
      .getBrowseEntryAEMContent()
      .pipe(
        catchError((err: APIError) => {
          return observableThrowError(err);
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((data: BrowseEntryConfigData) => {
        this.browseEntryAEMData = data;
        this.creditLevels = this.sharedBrowseService.getCreditLevelsDropdownValues(data);
        this.creditLevelMapping = this.sharedBrowseService.getCreditLevelMappingValues(data);
        this.billingLevels = this.sharedBrowseService.getBillingLevelsDropdownValues(data);
        this.billingTypeMapping = this.sharedBrowseService.getBillingTypeMappingValues(data);
        this.changeCreditLevelForm = this.sharedBrowseService.buildChangeCreditLevelForm();
        this.changeBillingLevelForm = this.sharedBrowseService.buildChangeBillingLevelForm();
        this.loadCurrentEntryState();
      });
  }

  /**
   * Change against Offer for Zip ad Billing Type must be disabled if in Upgrade Flow
   * @return void
   */
  private checkCustomerAvailable(): void {
    this.isCustomerAvailable = this.stateUtilService.getAuthCustomerId() ? true : false;
  }

  /**
   * gets the stored entry state
   * @returns BrowseEntry
   */
  private getStoredEntryState(): BrowseEntry {
    this.zipChangeSubmitted = false;
    this.billingLevelChangeSubmitted = false;
    this.creditLevelChangeSubmitted = true;
    this.changeState = null;
    return this.sharedBrowseService.getUserInputsConfigData();
  }

  /**
   * zip code changed
   * @returns void
   */
  private zipCodeChange(): void {
    if (this.selectedBrowseEntry && this.selectedBrowseEntry.addresses) {
      const billingAdress: Address = {
        addressid: this.selectedBrowseEntry.addresses.addressid,
        postalCode: this.selectedBrowseEntry.zip,
        stateCode: this.selectedBrowseEntry.stateCode,
        cityName: this.selectedBrowseEntry.cityName,
      };
      this.loadChangeEntryComponent();
      this.sharedBrowseService.updateBrowseZipCode(billingAdress);
      this.browseEntryChange.emit({ changeId: SHARED_BROWSE_CONSTANTS.zipCodeChanged });
    }
  }

  /** */

  /**
   * Method to set static button value
   * @returns TmoButton
   */
  private setButton(isPirmary: boolean, id?: string): TmoButton {
    const button: TmoButton = <TmoButton>{
      class: isPirmary ? 'btn btn-primary digital-btn' : 'btn btn-secondary digital-btn',
      id: id,
      type: isPirmary ? 'primary' : 'secondary',
    };
    return button;
  }

  public onShowZipCodeModal(): void {
    const zipCodeInput = <HTMLInputElement>document.getElementById('tmo-input-default-153');
    zipCodeInput.select();
    this.disableConfirm = true;
    this.apiError = false;
  }

  public closeModal(): void {
    this.changeState = null;
    this.changeZipForm.controls.zip.setValue(this.selectedBrowseEntry.zip);
    this.resetValueOnUpdate = false;
  }

  /**
   * Function triggered when zip code input has changed
   * @param value
   */
  public onInput(value: string): void {
    if (value && value.match(new RegExp(`.{${this.zipCodeFieldValidation.fieldAttributes.max}}`))) {
      this.validateZip();
    } else {
      this.disableConfirm = true;
    }
  }

  /**
   * Function used to get city name for zipcode
   */
  private validateZip(): void {
    this.showSpinner = true;
    this.disableConfirm = true;
    this.apiError = false;
    this.sharedBrowseService
      .validateAddress(this.changeZipForm)
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (resp: Address[]) => {
          const billingAddress = {
            addressid: resp[0].addressid,
            postalCode: resp[0].postalCode,
            cityName: resp[0].cityName,
            stateCode: resp[0].stateCode,
          };
          this.addressApiResponse = billingAddress;
          this.showSpinner = false;
          if (resp && resp.length) {
            this.disableConfirm = false;
          }
        },
        (error: APIError) => {
          this.disableConfirm = true;
          this.showSpinner = false;
          this.apiError = true;
        },
        () => {
          this.showSpinner = false;
        }
      );
  }
}
